import type { ProjectFile } from '../../core/hp/projectFile';
import type { Level } from '../../core/hp/types';
import { levelAltitude } from '../../state/floors';
import type { Store } from '../../state/store';
import { sessionColors } from '../participantColors';
import type { CollaborationState, CollaborationStatus, PresenceEvent, PresenceInput, PresenceView, SelectionTarget, SharedSnapshot, WorldPoint } from '../protocol';
import type { ParticipantLook, PresenceSource } from '../presenceSource';
import type { RtClock, RtConnection, RtStatus } from './connection';
import type { RtEvent } from './wire';

type Cursor = Pick<PresenceInput, 'floor' | 'mode' | 'cursor' | 'presence' | 'viewport' | 'selection'>;
type Member = { key: string; name: string; role: string; avatar: string };
type Moved = { viewMode: string; levelUniqueId: string; plainUniqueId: string; position: WorldPoint; yaw: number; at: number };
/** The app's `MoveData`, every field as its `JsonUtility` writes them. */
type MoveData = { position: WorldPoint; rotation: WorldPoint; viewMode: string; levelIndex: number; levelUniqueId: string; plainUniqueId: string; noPoint: boolean };
type Placed = Pick<PresenceEvent, 'mode' | 'cursor' | 'presence'> & { slot: number };
/** «{0} joins» or «{0} leaves». */
export type PresenceToast = { id: number; name: string; joined: boolean };

export interface LivePresenceDeps {
  store: Pick<Store, 'get'>;
  connection: Pick<RtConnection, 'send' | 'canSend'>;
  clock: Pick<RtClock, 'now' | 'setTimeout' | 'clearTimeout'>;
  /** The room's stored file: wall sides and ceilings have their ids there only. */
  file: () => ProjectFile | null;
  retry: () => void;
}

const MIN_MOVE_SQR = 1e-4, MIN_ROTATION_DEG = 0.1;
/** A leave waits this long: the same account back by then (a rejoin, a 4008 displacement) is no news. */
export const REJOIN_GRACE_MS = 3000;
/** A peer works for this long after their last Move or DomainSelection. */
export const WORKING_MS = 1500;
const VIEW_MODES: Record<PresenceView['kind'], string> = { floor: 'Floor2D', ceiling: 'Ceiling2D', wall: 'Wall2D', orbit: 'Panorama', walk: 'ThreeD' };
const STATUS: Record<RtStatus, CollaborationStatus> = { connecting: 'connecting', live: 'ready', rejoining: 'reconnecting', reconnecting: 'reconnecting' };
const ROLES: Record<string, ParticipantLook['role']> = { owner: 'owner', can_view: 'viewer' };
/** The web's world is Unity's with z turned, both ways. */
const turned = (point: WorldPoint): WorldPoint => ({ x: point.x, y: point.y, z: -point.z });
const text = (value: unknown) => typeof value === 'string' ? value : '';
const finite = (value: unknown) => typeof value === 'number' && Number.isFinite(value) ? value : 0;
const vector = (value: unknown): WorldPoint => { const v = (value ?? {}) as Record<string, unknown>; return { x: finite(v.x), y: finite(v.y), z: finite(v.z) }; };
const sameView = (a: Pick<MoveData, 'viewMode' | 'levelUniqueId' | 'plainUniqueId'>, b: Pick<MoveData, 'viewMode' | 'levelUniqueId' | 'plainUniqueId'>) =>
  a.viewMode === b.viewMode && a.levelUniqueId === b.levelUniqueId && a.plainUniqueId === b.plainUniqueId;
const viewKey = (data: Pick<MoveData, 'viewMode' | 'levelUniqueId' | 'plainUniqueId'>) => `${data.viewMode}|${data.levelUniqueId}|${data.plainUniqueId}`;
const ZERO: WorldPoint = { x: 0, y: 0, z: 0 };
const memberOf = (person: Record<string, unknown>, playerId: number): Member =>
  ({ key: text(person.accountId) || `p#${playerId}`, name: text(person.userName), role: text(person.role), avatar: text(person.avatarUrl) });

/**
 * The app's peers in its live room as a presence source, and the web's own `Move` as the app's `PlayerMovementSender`
 * writes it. The roster comes from `SessionState` and `PlayerJoined`/`PlayerLeft`, each player's last `Move` and
 * `DomainSelection` are kept as sent, and the floor, wall side and room they name are found when read.
 */
export class LivePresence implements PresenceSource {
  readonly kind = 'live';
  private members = new Map<number, Member>();
  private moves = new Map<number, Moved>();
  /** In claim order, which `participants` follow so the holders of one target come in it. */
  private selections = new Map<number, string>();
  private self: number | null = null;
  private snapshot: SharedSnapshot | null = null;
  private status: CollaborationStatus = 'connecting';
  private version = 0;
  private cached: { version: number; slot: number; value: CollaborationState } | null = null;
  private listeners = new Set<() => void>();
  /** The last Move sent, what the next one is measured against. */
  private last: MoveData | null = null;
  /** The last Move asked for, sent or not: after a `SessionState` the peers get the web where it is now. */
  private wanted: MoveData | null = null;
  /** The last point in each 2D view, by `viewKey`. */
  private points = new Map<string, WorldPoint>();
  /** By `DomainSelection` id, what it names in the current snapshot. */
  private targets = new Map<string, SelectionTarget | null>();
  private readonly colors = sessionColors;
  private toastList: readonly PresenceToast[] = [];
  private toastId = 0;
  /** Leaves waiting out `REJOIN_GRACE_MS`, by member key. */
  private leaving = new Map<string, unknown>();
  /** When each player last moved or selected, while that is under `WORKING_MS` old; one timer tells the next end. */
  private acted = new Map<number, number>();
  private actedTimer: unknown = null;

  constructor(private readonly deps: LivePresenceDeps) {}

  get clientId(): string { return this.self === null ? '' : String(this.self); }

  get(): CollaborationState {
    const slot = this.deps.store.get().activeLevelIndex;
    if (this.cached?.version === this.version && this.cached.slot === slot) return this.cached.value;
    const order = new Set([...this.selections.keys(), ...this.members.keys()]);
    const participants = [...order].flatMap(playerId => { const member = this.members.get(playerId);
      return member && playerId !== this.self ? [this.participant(playerId, member)] : []; });
    const value: CollaborationState = { status: this.status, snapshot: this.snapshot, participants, previews: [], error: null, pendingCommandId: null };
    this.cached = { version: this.version, slot, value };
    return value;
  }

  subscribe = (fn: () => void): (() => void) => { this.listeners.add(fn); return () => { this.listeners.delete(fn); }; };

  retry = async (): Promise<void> => { this.deps.retry(); };

  /** The app's `MultiplayerJoinToastController`: joins and leaves after the roster `SessionState` brought, newest last. */
  toasts = (): readonly PresenceToast[] => this.toastList;

  dismiss = (id: number): void => {
    const next = this.toastList.filter(toast => toast.id !== id);
    if (next.length !== this.toastList.length) { this.toastList = next; this.notify(); }
  };

  look(userId: string): ParticipantLook | null {
    let found: Member | null = null, working = false;
    for (const [playerId, member] of this.members) if (member.key === userId) { found ??= member; working ||= this.acted.has(playerId); }
    return found ? { ...this.colors.colorOf(userId), agent: false, role: ROLES[found.role] ?? 'editor', working, ...(found.avatar ? { avatar: found.avatar } : {}) } : null;
  }

  /** `SessionState`: the roster, the web's own player, and the last Move again, as its peers forgot it. */
  rebase(state: RtEvent): void {
    const people = Array.isArray(state.presence) ? state.presence as Record<string, unknown>[] : [];
    this.self = typeof state.playerId === 'number' ? state.playerId : null;
    this.members = new Map(people.flatMap(person => typeof person?.playerId === 'number' ? [[person.playerId, memberOf(person, person.playerId)] as const] : []));
    for (const kept of [this.moves, this.selections, this.acted]) for (const playerId of [...kept.keys()]) if (!this.members.has(playerId)) kept.delete(playerId);
    for (const key of [...this.leaving.keys()]) if (this.present(key)) this.stay(key);
    this.last = null;
    if (this.wanted) this.post(this.wanted);
    this.changed(true);
  }

  receive(event: RtEvent): void {
    const playerId = event.playerId, data = (event.data ?? {}) as Record<string, unknown>;
    if (typeof playerId !== 'number' || playerId === this.self) return;
    if (event.type === 'PlayerLeft') {
      const member = this.members.get(playerId);
      this.members.delete(playerId); this.moves.delete(playerId); this.selections.delete(playerId); this.acted.delete(playerId);
      if (member && !this.present(member.key) && !this.own(member.key) && !this.leaving.has(member.key)) {
        this.leaving.set(member.key, this.deps.clock.setTimeout(() => {
          this.leaving.delete(member.key);
          if (!this.present(member.key)) { this.toast(member.name, false); this.notify(); }
        }, REJOIN_GRACE_MS));
      }
      this.changed(true);
    } else if (event.type === 'PlayerJoined') {
      const member = memberOf(event, playerId), news = !this.present(member.key) && !this.own(member.key) && !this.leaving.has(member.key);
      this.stay(member.key);
      this.members.set(playerId, member);
      if (news) this.toast(member.name, true);
      this.changed(true);
    } else if (event.type === 'Move' && data.noPoint !== true) {
      this.moves.set(playerId, { viewMode: text(data.viewMode), levelUniqueId: text(data.levelUniqueId), plainUniqueId: text(data.plainUniqueId),
        position: vector(data.position), yaw: vector(data.rotation).y * Math.PI / 180, at: this.deps.clock.now() });
      this.act(playerId);
      this.changed();
    } else if (event.type === 'DomainSelection') {
      // The app's SelectionHolders.Claim: the same target again keeps the place, another one (or none) goes to the end.
      const targetId = text(data.targetId);
      if (this.selections.get(playerId) !== targetId) { this.selections.delete(playerId); if (targetId) this.selections.set(playerId, targetId); }
      this.act(playerId);
      this.changed();
    }
  }

  private act(playerId: number): void {
    this.acted.set(playerId, this.deps.clock.now());
    this.actedTimer ??= this.deps.clock.setTimeout(this.rest, WORKING_MS);
  }

  /** Lets go of who has not acted for `WORKING_MS` and waits for the next of them. */
  private rest = (): void => {
    const now = this.deps.clock.now();
    let next = Infinity, ended = false;
    for (const [playerId, at] of this.acted) {
      if (now - at >= WORKING_MS) { this.acted.delete(playerId); ended = true; } else next = Math.min(next, at + WORKING_MS);
    }
    this.actedTimer = next < Infinity ? this.deps.clock.setTimeout(this.rest, next - now) : null;
    if (ended) this.notify();
  };

  /** The scene the store holds now, the one ids are found in. */
  accept(snapshot: SharedSnapshot): void { this.snapshot = snapshot; this.targets.clear(); this.changed(); }

  setStatus(status: RtStatus, ended: boolean): void {
    const next = ended ? 'offline' : STATUS[status];
    if (next !== this.status) { this.status = next; this.changed(); }
  }

  clear(): void {
    this.members.clear(); this.moves.clear(); this.selections.clear(); this.acted.clear();
    if (this.actedTimer !== null) { this.deps.clock.clearTimeout(this.actedTimer); this.actedTimer = null; }
    for (const key of [...this.leaving.keys()]) this.stay(key);
    this.toastList = [];
    this.changed(true);
  }

  private present(key: string): boolean {
    for (const member of this.members.values()) if (member.key === key) return true;
    return false;
  }

  /** The web's own account, in this tab or another. */
  private own(key: string): boolean {
    return this.self !== null && this.members.get(this.self)?.key === key;
  }

  /** A leave of `key` no longer to be told. */
  private stay(key: string): void {
    if (!this.leaving.has(key)) return;
    this.deps.clock.clearTimeout(this.leaving.get(key));
    this.leaving.delete(key);
  }

  private toast(name: string, joined: boolean): void {
    this.toastList = [...this.toastList, { id: ++this.toastId, name, joined }];
  }

  /** The session is over: the room's colours go with it. */
  dispose(): void {
    this.clear();
    this.colors.reset();
    this.last = null; this.wanted = null; this.points.clear();
  }

  sendCursor(input: Cursor): void {
    const slot = input.floor.slot, levels = this.snapshot?.scene.levels ?? [], level = levels[slot];
    if (!level) return;
    const editor = this.deps.store.get();
    const view: PresenceView = input.presence?.view ?? (editor.mode === 'scene' ? { kind: editor.firstPerson ? 'walk' : 'orbit' }
      : editor.planSurface === 'wall' ? { kind: 'wall', wallId: editor.wallView?.wallId ?? '', side: editor.wallView?.side ?? 'front' }
      : editor.planSurface === 'ceiling' ? { kind: 'ceiling', roomIndex: -1 } : { kind: 'floor' });
    const base = { viewMode: VIEW_MODES[view.kind], levelIndex: slot, levelUniqueId: level.uniqueId ?? '', plainUniqueId: this.plainOf(level, view) };
    if (view.kind === 'orbit' || view.kind === 'walk') {
      const pose = input.presence?.pose;
      if (!pose) return;
      this.send({ ...base, position: turned(pose.position), rotation: { x: 0, y: ((pose.yaw * 180 / Math.PI) % 360 + 360) % 360, z: 0 }, noPoint: false });
    } else if (input.cursor && input.presence) {
      const position = turned(input.cursor), altitude = levelAltitude(levels, slot);
      // The app's wall sides and ceilings stand at their floor's altitude (`LevelController.UpdateAltitude`); its floor plane is y = 0.
      if (view.kind === 'wall') position.y += altitude;
      if (view.kind === 'ceiling') position.y = altitude + this.ceilingHeight(level, view.roomIndex, input.cursor.y);
      if (view.kind === 'wall') this.points.set(viewKey(base), position);
      this.send({ ...base, position, rotation: ZERO, noPoint: false });
    } else if (!this.last || !sameView(this.last, base)) {
      // The app's `AnnounceWithoutPoint`: its receivers draw nothing for it, older ones a mark at this point.
      this.send({ ...base, position: this.points.get(viewKey(base)) ?? this.centreOf(level, view, levelAltitude(levels, slot), input.viewport), rotation: ZERO, noPoint: true });
    }
  }

  /** Where an older app draws a view announced without a point: the wall's middle, else the plan's centre in view. */
  private centreOf(level: Level, view: PresenceView, altitude: number, viewport: Cursor['viewport']): WorldPoint {
    const wall = view.kind === 'wall' ? level.walls.find(value => value.id === view.wallId) : undefined;
    if (wall) return turned({ x: wall.center.x, y: altitude + wall.height / 2, z: wall.center.z });
    return viewport && 'centre' in viewport ? turned({ x: viewport.centre.x, y: 0, z: viewport.centre.z }) : ZERO;
  }

  private send(data: MoveData): void {
    this.wanted = data;
    const last = this.last;
    if (!data.noPoint && last && !last.noPoint && sameView(last, data)
      && (data.position.x - last.position.x) ** 2 + (data.position.y - last.position.y) ** 2 + (data.position.z - last.position.z) ** 2 < MIN_MOVE_SQR
      && Math.abs(data.rotation.y - last.rotation.y) < MIN_ROTATION_DEG) return;
    this.post(data);
  }

  /** Never queued: a Move the socket cannot take now is not sent, and the next one goes in its place. */
  private post(data: MoveData): boolean {
    if (!this.deps.connection.canSend || !this.deps.connection.send([{ type: 'Move', data }])) return false;
    this.last = data;
    return true;
  }

  /** The room's own `<Ceiling Height>` in the stored file; the parser gives `Room.height` the floor's. */
  private ceilingHeight(level: Level, roomIndex: number, fallback: number): number {
    const file = this.deps.file(), room = level.rooms.find(value => value.index === roomIndex);
    const element = room?.uniqueId && file ? file.byId(room.uniqueId) : undefined;
    const height = Number(element && file!.children(element, 'Ceiling')[0]?.attribute('Height'));
    return Number.isFinite(height) && height > 0 ? height : room?.height ?? fallback;
  }

  /** A wall view's Front or Back id, a ceiling view's room's Ceiling id, as the file holds them; '' elsewhere. */
  private plainOf(level: Level, view: PresenceView): string {
    const file = this.deps.file();
    const owner = view.kind === 'wall' ? level.walls.find(wall => wall.id === view.wallId)?.uniqueId
      : view.kind === 'ceiling' ? level.rooms.find(room => room.index === view.roomIndex)?.uniqueId : undefined;
    const element = owner && file ? file.byId(owner) : undefined;
    if (!element || !file) return '';
    const tag = view.kind === 'ceiling' ? 'Ceiling' : view.kind === 'wall' && view.side === 'back' ? 'Back' : 'Front';
    return file.children(element, tag)[0]?.attribute('UniqueId') ?? '';
  }

  private participant(playerId: number, member: Member): PresenceEvent {
    const moved = this.moves.get(playerId), placed = moved ? this.place(moved) : null, snapshot = this.snapshot;
    const targetId = this.selections.get(playerId), target = targetId ? this.target(targetId) : null;
    return { protocol: 1, documentId: snapshot?.documentId ?? '', generation: snapshot?.generation ?? 0, clientId: String(playerId), seq: 0,
      floor: { layoutVersion: snapshot?.layoutVersion ?? 0, slot: placed?.slot ?? -1 }, mode: placed?.mode ?? 'plan', cursor: placed?.cursor ?? null,
      presence: placed?.presence ?? null, viewport: null, selection: target ? [target] : [], preview: null,
      userId: member.key, displayName: member.name, sentAt: moved?.at ?? 0 };
  }

  /** The app's `PlayerMovementReceiver` and `PlayerController.DecideVisibility`: an empty levelUniqueId is the floor in view. */
  private place(moved: Moved): Placed | null {
    const levels = this.snapshot?.scene.levels ?? [];
    const slot = moved.levelUniqueId ? levels.findIndex(level => level.uniqueId === moved.levelUniqueId) : this.deps.store.get().activeLevelIndex;
    const level = levels[slot], point = turned(moved.position);
    if (!level) return null;
    if (moved.viewMode === 'Wall2D' || moved.viewMode === 'Ceiling2D') point.y -= levelAltitude(levels, slot);
    if (moved.viewMode === 'Floor2D') return { slot, mode: 'plan', cursor: point, presence: { view: { kind: 'floor' }, pose: null } };
    if (moved.viewMode === 'Wall2D' || moved.viewMode === 'Ceiling2D') {
      const view = this.plane(level, moved.plainUniqueId);
      if (!view || (view.kind === 'wall') !== (moved.viewMode === 'Wall2D')) return null;
      return { slot, mode: view.kind === 'wall' ? 'wall' : 'plan', cursor: point, presence: { view, pose: null } };
    }
    return { slot, mode: 'scene', cursor: null, presence: { view: { kind: moved.viewMode === 'Panorama' ? 'orbit' : 'walk' }, pose: { position: point, yaw: moved.yaw } } };
  }

  private plane(level: Level, plainUniqueId: string): PresenceView | null {
    const file = this.deps.file(), element = plainUniqueId && file ? file.byId(plainUniqueId) : undefined;
    const owner = element && file!.parent(element), ownerId = owner?.attribute('UniqueId');
    if (!element || !owner || !ownerId) return null;
    if (element.tag === 'Front' || element.tag === 'Back') {
      const wall = owner.tag === 'Wall' ? level.walls.find(value => value.uniqueId === ownerId) : undefined;
      return wall ? { kind: 'wall', wallId: wall.id, side: element.tag === 'Front' ? 'front' : 'back' } : null;
    }
    const room = element.tag === 'Ceiling' && owner.tag === 'Room' ? level.rooms.find(value => value.uniqueId === ownerId) : undefined;
    return room ? { kind: 'ceiling', roomIndex: room.index } : null;
  }

  /** What a `DomainSelection` names: a piece, a wall, an opening or a room, or a part of one (a wall side, a room's floor). */
  private target(uniqueId: string): SelectionTarget | null {
    let found = this.targets.get(uniqueId);
    if (found === undefined) { found = this.find(uniqueId); this.targets.set(uniqueId, found); }
    return found;
  }

  private find(uniqueId: string): SelectionTarget | null {
    for (const level of this.snapshot?.scene.levels ?? []) {
      const item = level.items.find(value => value.uniqueId === uniqueId);
      if (item) return { kind: 'item', id: item.id };
      const wall = level.walls.find(value => value.uniqueId === uniqueId);
      if (wall) return { kind: 'wall', id: wall.id };
      const opening = level.openings.find(value => value.uniqueId === uniqueId);
      if (opening) return { kind: 'opening', id: opening.id };
      const room = level.rooms.find(value => value.uniqueId === uniqueId);
      if (room) return { kind: 'room', index: room.index };
    }
    const file = this.deps.file(), element = file?.byId(uniqueId), parent = element && file!.parent(element);
    const owner = parent?.attribute('UniqueId');
    return owner ? this.find(owner) : null;
  }

  private changed(roster = false): void {
    this.version++;
    if (roster) this.colors.sync([...this.members].map(([playerId, member]) => ({ key: member.key, order: playerId })));
    this.notify();
  }

  /** The toasts changed and nothing `get()` reads. */
  private notify(): void {
    for (const fn of [...this.listeners]) fn();
  }
}
